import React from "react";
import {
  Shield,
  MessageSquare,
  ChevronRight,
  ChevronLeft,
  Send,
} from "lucide-react";
import { Senior, FeedbackMessage } from "../../types";

interface FeedbackPageProps {
  seniors: Senior[];
  followedPaths: number[];
  maskName: (name: string) => string;
  feedbackMessages: Record<number, FeedbackMessage[]>;
  setFeedbackMessages: (messages: Record<number, FeedbackMessage[]>) => void;
  currentChat: number | null;
  setCurrentChat: (chatId: number | null) => void;
  setCurrentPage: (page: string) => void;
}

const FeedbackPage: React.FC<FeedbackPageProps> = ({
  seniors,
  followedPaths,
  maskName,
  feedbackMessages,
  setFeedbackMessages,
  currentChat,
  setCurrentChat,
  setCurrentPage,
}) => {
  const followedSeniors = seniors.filter((senior) =>
    followedPaths.includes(senior.id)
  );

  if (followedSeniors.length === 0) {
    return (
      <div className="p-6">
        <h2 className="text-2xl font-bold mb-6">피드백</h2>
        <div className="bg-gray-50 p-8 rounded-xl text-center">
          <MessageSquare className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-600 mb-2">아직 따라가는 선배가 없습니다</p>
          <p className="text-sm text-gray-500 mb-4">
            선배의 경로를 따라가기 하면 소통할 수 있습니다
          </p>
          <button
            onClick={() => setCurrentPage("recommendation")}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg text-sm hover:bg-blue-700"
          >
            선배 찾아보기
          </button>
        </div>
      </div>
    );
  }

  if (currentChat) {
    const senior = seniors.find((s) => s.id === currentChat);
    const messages = feedbackMessages[currentChat] || [];

    return (
      <div className="flex flex-col h-screen">
        <div className="bg-white border-b p-4 flex items-center">
          <button onClick={() => setCurrentChat(null)} className="mr-3">
            <ChevronLeft className="w-6 h-6" />
          </button>
          <div className="flex-1">
            <h3 className="font-semibold flex items-center">
              {senior && maskName(senior.name)}
              <Shield className="w-4 h-4 ml-1 text-gray-400" />
            </h3>
            <p className="text-sm text-gray-600">
              {senior && senior.company}
            </p>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4 bg-gray-50">
          {messages.length === 0 ? (
            <div className="text-center text-gray-500 mt-8">
              <p className="mb-2">선배님과의 대화를 시작해보세요!</p>
              <p className="text-sm">궁금한 점을 편하게 물어보세요 😊</p>
            </div>
          ) : (
            <div className="space-y-3">
              {messages.map((msg, idx) => (
                <div
                  key={idx}
                  className={`flex ${
                    msg.sender === "user" ? "justify-end" : "justify-start"
                  }`}
                >
                  <div
                    className={`max-w-[70%] p-3 rounded-lg ${
                      msg.sender === "user"
                        ? "bg-blue-600 text-white"
                        : "bg-white border border-gray-200"
                    }`}
                  >
                    <p className="text-sm">{msg.text}</p>
                    <p
                      className={`text-xs mt-1 ${
                        msg.sender === "user"
                          ? "text-blue-100"
                          : "text-gray-500"
                      }`}
                    >
                      {msg.time}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-white border-t p-4">
          <div className="flex gap-2">
            <input
              type="text"
              placeholder="메시지를 입력하세요..."
              className="flex-1 p-3 border border-gray-300 rounded-lg"
              onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                const chatId = currentChat;
                if (chatId === null) return;

                const target = e.target as HTMLInputElement;
                const trimmedValue = target.value.trim();
                if (e.key === "Enter" && trimmedValue) {
                  // const newMessage = {
                  //   sender: "user",
                  //   text: trimmedValue,
                  //   time: new Date().toLocaleTimeString("ko-KR", {
                  //     hour: "2-digit",
                  //     minute: "2-digit",
                  //   }),
                  // };
                  // setFeedbackMessages((prev) => ({
                  //   ...prev,
                  //   [chatId]: [...(prev[chatId] || []), newMessage],
                  // }));
                  target.value = "";

                  // setTimeout(() => {
                  // const autoReply = {
                  //   sender: "senior",
                  //   text: "안녕하세요! 질문 감사합니다. 제 경험을 바탕으로 도움을 드릴게요.",
                  //   time: new Date().toLocaleTimeString("ko-KR", {
                  //     hour: "2-digit",
                  //     minute: "2-digit",
                  //   }),
                  // };
                  // setFeedbackMessages((prev) => ({
                  //   ...prev,
                  //   [chatId]: [...(prev[chatId] || []), autoReply],
                  // }));
                  // }, 1000);
                }
              }}
            />
            <button className="bg-blue-600 text-white p-3 rounded-lg hover:bg-blue-700">
              <Send className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <h2 className="text-2xl font-bold mb-6">피드백</h2>

      <div className="bg-blue-50 p-4 rounded-xl mb-6">
        <div className="flex items-start">
          <Shield className="w-5 h-5 text-blue-600 mr-2 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-blue-800">
              선배님과의 모든 대화는 앱 내에서만 이루어지며, 개인정보는
              안전하게 보호됩니다.
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-3">
        {followedSeniors.map((senior) => (
          <div
            key={senior.id}
            onClick={() => setCurrentChat(senior.id)}
            className="bg-white border border-gray-200 rounded-xl p-4 hover:shadow-md transition-shadow cursor-pointer"
          >
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <h3 className="font-semibold flex items-center">
                  {maskName(senior.name)}
                  <Shield className="w-4 h-4 ml-1 text-gray-400" />
                </h3>
                <p className="text-sm text-gray-600">
                  {senior.company} · {senior.position}
                </p>
                <div className="flex gap-2 mt-2">
                  {senior.badges.map((badge, idx) => (
                    <span
                      key={idx}
                      className="bg-yellow-100 text-yellow-700 px-2 py-1 rounded text-xs"
                    >
                      🏆 {badge}
                    </span>
                  ))}
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-400" />
            </div>
            {feedbackMessages[senior.id] &&
              feedbackMessages[senior.id].length > 0 && (
                <div className="mt-2 pt-2 border-t">
                  <p className="text-xs text-gray-500">
                    마지막 메시지:{" "}
                    {
                      feedbackMessages[senior.id][
                        feedbackMessages[senior.id].length - 1
                      ].time
                    }
                  </p>
                </div>
              )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default FeedbackPage;